import { useRef, useEffect, useState } from "react";

/**
 * Custom hook to track whether a section is in view or below the viewport.
 * Returns true when the section is visible or has been scrolled past (below viewport).
 * Returns false when the section is above the viewport (not yet reached).
 *
 * Useful for showing sticky CTAs that should only appear once user reaches or passes a section.
 *
 * @param threshold - Percentage of section that must be visible to trigger (default: 0.1)
 * @returns [ref, showContent] - Ref to attach to the element and boolean indicating if content should show
 */
export default function useSectionScrollPosition<T extends HTMLElement = HTMLDivElement>(
  threshold: number = 0.1,
): [React.RefObject<T | null>, boolean] {
  const [ showContent, setShowContent ] = useState(false);
  const sectionRef = useRef<T>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([ entry ]) => {
        if (entry.isIntersecting) {
          // Section is in view, show content
          setShowContent(true);
        } else {
          // Section is not in view, check if we're above or below it
          const rect = entry.boundingClientRect;
          // If top is positive, we're above the section (haven't reached it yet)
          // If top is negative, we're below the section (scrolled past it)
          setShowContent(rect.top < 0);
        }
      },
      {
        threshold,
      },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [ threshold ]);

  return [ sectionRef, showContent ];
}
